import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormRadioGroup from 'components/KNForm/KNFormRadioGroup'
import KNFormText from 'components/KNForm/KNFormText'
import { analyticsEvent } from 'global/helpers/analytics'
import { processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { BUSINESS_ENTITIES_QUERY_KEY, createBusinessEntity, editBusinessEntity } from './CarrierProfile.service'
import { type BusinessEntity, type BusinessEntityFormValues, ProfileType } from './CarrierProfile.types'

export interface CreateOrEditBusinessEntityDialogPayload {
  businessEntity: BusinessEntity
}

export interface CreateOrEditBusinessEntityDialogProps {
  readonly payload?: CreateOrEditBusinessEntityDialogPayload
  readonly open: boolean
  readonly onAction: () => void
  readonly onClose: () => void
}

const CreateOrEditBusinessEntityDialog = ({
  payload,
  open,
  onAction,
  onClose,
}: CreateOrEditBusinessEntityDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, formState, setError } = useForm<BusinessEntityFormValues>()
  const queryClient = useQueryClient()
  const createBusinessEntityMutation = useMutation({
    mutationFn: async (data: BusinessEntityFormValues) => {
      await createBusinessEntity(data)
    },
    onError: (error) => setError('root', processServerErrorMessages(error)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [BUSINESS_ENTITIES_QUERY_KEY] }),
  })
  const updateBusinessEntityMutation = useMutation({
    mutationFn: async (data: BusinessEntityFormValues) => {
      await editBusinessEntity(payload?.businessEntity.id ?? '', data)
    },
    onError: (error) => setError('root', processServerErrorMessages(error)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [BUSINESS_ENTITIES_QUERY_KEY] }),
  })

  useEffect(() => {
    if (open) {
      reset(
        processDefaultValues({
          location: payload?.businessEntity.location,
          type: payload?.businessEntity.type,
          registrationTaxNumber: payload?.businessEntity.registrationTaxNumber,
        }),
      )
    }
  }, [open])

  const onSubmit: SubmitHandler<BusinessEntityFormValues> = async (data: BusinessEntityFormValues): Promise<void> => {
    if (payload) {
      await updateBusinessEntityMutation.mutateAsync(data)
      analyticsEvent('polestar_carrier_profile_business_entity_updated')
    } else {
      await createBusinessEntityMutation.mutateAsync(data)
      analyticsEvent('polestar_carrier_profile_business_entity_created')
    }
    onAction()
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={payload ? t('screens.cs.carrier_profile.business_entities.edit_business_entity') : t('screens.cs.carrier_profile.business_entities.add_business_entity')}
      actions={
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          loading={formState.isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {t('general.save')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Stack spacing={2}>
        <KNFormRadioGroup
          name="type"
          control={control}
          rules={{
            required: t('form.validation.required'),
          }}
          options={Object.keys(ProfileType).map((type) => {
            return {
              value: type,
              label: t(`screens.cs.carrier_profile.types.${type}`),
            }
          })}
        />
        <KNFormText
          name="location"
          label={t('screens.cs.carrier_profile.business_entities.location')}
          control={control}
          rules={{
            required: t('form.validation.required'),
          }}
        />
        <KNFormText
          name="registrationTaxNumber"
          label={t('screens.cs.carrier_profile.company.registration_tax_number')}
          control={control}
          rules={{
            required: t('form.validation.required'),
          }}
        />
      </Stack>
    </KNDialog>
  )
}

export default CreateOrEditBusinessEntityDialog
